import { computed, nextTick, reactive, ref } from 'vue';
import type { IShortcutKeyActionable } from 'src/models';
import type { GIMovieList } from 'src/models/geoItem';
import type { MovieList } from 'src/models/movie';
import type { MoviePlayerParams, SwitchToFullModeParams } from 'src/models/moviePlayer';

interface MoviePlayerVisibilityState {
  moviePlayerType: 'full' | 'comp';
  moviePlayerMovieLists: GIMovieList[];
  moviePlayerFullMovieListId: string | null;
  moviePlayerCompMovieListId: string | null;
}

export type MoviePlayerStore = ReturnType<typeof useMoviePlayer>;

export function useMoviePlayer(callbacks: {
  getMovieSearchResults: () => GIMovieList[];
}) {
  const refMoviePlayer = ref<IShortcutKeyActionable>();
  const refMoviePlayerCompareMode = ref<IShortcutKeyActionable>();

  const moviePlayerState = reactive<MoviePlayerVisibilityState>({
    moviePlayerType: 'full',
    moviePlayerMovieLists: [],
    moviePlayerFullMovieListId: null,
    moviePlayerCompMovieListId: null,
  });

  const showMoviePlayerFullMode = computed(() => {
    return moviePlayerState.moviePlayerMovieLists.length > 0 &&
      !!moviePlayerState.moviePlayerFullMovieListId &&
      moviePlayerState.moviePlayerType === 'full';
  });
  const showMoviePlayerCompareMode = computed(() => {
    return moviePlayerState.moviePlayerMovieLists.length > 0 &&
      !!moviePlayerState.moviePlayerCompMovieListId &&
      moviePlayerState.moviePlayerType === 'comp';
  });

  const showAndPlayMoviePlayer = (movieList: MovieList | GIMovieList): void => {
    // 常に三面モードで開く
    moviePlayerState.moviePlayerType = 'full';
    moviePlayerState.moviePlayerMovieLists = callbacks.getMovieSearchResults();
    moviePlayerState.moviePlayerFullMovieListId = movieList.id;
  };
  const afterMoviePlayClick = (obj: MovieList | GIMovieList): void => {
    showAndPlayMoviePlayer(obj);
  };
  const closeMoviePlayer = (): void => {
    moviePlayerState.moviePlayerMovieLists = [];
    moviePlayerState.moviePlayerFullMovieListId = null;
    moviePlayerState.moviePlayerCompMovieListId = null;
  };
  const tryControlMoviePlayer = (srcKey: string): void => {
    // moviePlayer側でやろうとすると表示していないときも拾ってしまって
    // 知らない内に何かが起こってそうなので、top側で制御する
    if (showMoviePlayerFullMode.value && refMoviePlayer.value) {
      refMoviePlayer.value.shortcutKeyAction(srcKey);
    } else if (showMoviePlayerCompareMode.value && refMoviePlayerCompareMode.value) {
      refMoviePlayerCompareMode.value.shortcutKeyAction(srcKey);
    }
  };
  const switchMoviePlayerToFullMode = (obj: SwitchToFullModeParams): void => {
    moviePlayerState.moviePlayerType = 'full';
    moviePlayerState.moviePlayerMovieLists = callbacks.getMovieSearchResults();
    moviePlayerState.moviePlayerFullMovieListId = null;
    nextTick(() => {
      moviePlayerState.moviePlayerFullMovieListId = obj.leftMovieListId;
    });
  };
  const switchMoviePlayerToCompareMode = (obj: MoviePlayerParams): void => {
    moviePlayerState.moviePlayerType = 'comp';
    moviePlayerState.moviePlayerMovieLists = obj.movieLists;
    moviePlayerState.moviePlayerCompMovieListId = null;
    nextTick(() => {
      moviePlayerState.moviePlayerCompMovieListId = obj.movieListId;
    });
  };

  const store = {
    moviePlayerState,
    refMoviePlayer,
    refMoviePlayerCompareMode,
    showMoviePlayerFullMode,
    showMoviePlayerCompareMode,
    afterMoviePlayClick,
    closeMoviePlayer,
    tryControlMoviePlayer,
    switchMoviePlayerToFullMode,
    switchMoviePlayerToCompareMode,
  };
  return store;
}
